/**
 * Dungeon System Types
 * Sistema de dungeons multi-sala para modo história
 * Transforma cada capítulo em uma experiência de "dungeon crawler"
 */

import * as THREE from "three";
import { TerrainConfig, ArenaShape } from "../waveGenerator";
import { BossConfig } from "./storyWaveGenerator";

// ============================================
// TIPOS BASE DO SISTEMA DE DUNGEON
// ============================================

/**
 * Tipos de sala disponíveis no sistema de dungeon
 */
export type RoomType = 
  | "combat"     // Sala de combate padrão
  | "miniboss"   // Sala com mini-boss
  | "boss"       // Sala do boss final do capítulo
  | "treasure"   // Sala secreta com recompensas
  | "corridor"   // Corredor de transição (sem inimigos)
  | "rest"       // Sala de descanso com power-ups
  | "survival"   // Sala de sobrevivência - resistir por tempo
  | "generator"  // Sala de defesa de gerador/alvo

/**
 * Tipos de bloqueio para portas
 */
export type DoorLockType = 
  | "enemies"    // Abre quando todos os inimigos morrem
  | "switch"     // Precisa ativar um switch
  | "key"        // Precisa de uma chave (encontrada em sala anterior)
  | "secret"     // Porta secreta (precisa descobrir)
  | "timer"      // Abre após tempo de sobrevivência
  | "none"       // Sempre aberta

/**
 * Estado atual de uma porta
 */
export type DoorState = 
  | "locked"     // Fechada e bloqueada
  | "unlocking"  // Em animação de desbloqueio
  | "unlocked"   // Desbloqueada, pronta para abrir
  | "opening"    // Em animação de abertura
  | "open"       // Completamente aberta

/**
 * Direções de portas
 */
export type DoorDirection = "NORTH" | "SOUTH" | "EAST" | "WEST";

// ============================================
// CONFIGURAÇÃO DE ONDAS POR SALA
// ============================================

/**
 * Tipos de inimigos disponíveis
 */
export type EnemyType = 
  | "normal"     // Inimigo padrão
  | "fast"       // Rápido mas fraco
  | "heavy"      // Lento mas resistente
  | "sniper"     // Atira de longe
  | "kamikaze"   // Corre em direção ao jogador
  | "boss"       // Boss do capítulo

/**
 * Configuração de uma wave dentro de uma sala
 */
export interface RoomWaveConfig {
  /** Número de inimigos nesta wave */
  enemyCount: number;
  /** Tipos de inimigos que podem spawnar */
  enemyTypes: EnemyType[];
  /** Delay em ms antes de iniciar esta wave */
  delayMs: number;
  /** Configuração do boss (se for wave de boss) */
  bossConfig?: BossConfig;
  /** Multiplicador de dificuldade para esta wave específica */
  difficultyMultiplier?: number;
}

// ============================================
// PERIGOS AMBIENTAIS
// ============================================

/**
 * Tipos de perigos ambientais
 */
export type HazardType = 
  | "steam_vent"    // Vapor que causa dano
  | "spikes"        // Espinhos no chão
  | "fire_pit"      // Fosso de fogo
  | "electric_floor" // Piso eletrificado
  | "falling_debris" // Destroços caindo
  | "acid_pool"     // Poça de ácido
  | "ice_patch"     // Área de gelo (escorregadia)
  | "poison_cloud"  // Nuvem de veneno
  | "lava_geyser"   // Gêiser de lava

/**
 * Configuração de um perigo ambiental
 */
export interface EnvironmentHazard {
  type: HazardType;
  /** Posições onde o hazard aparece */
  positions: THREE.Vector3[];
  /** Dano causado por tick */
  damage: number;
  /** Intervalo entre danos em ms */
  intervalMs: number;
  /** Tempo de aviso antes de ativar (para hazards intermitentes) */
  warningMs?: number;
  /** Raio de área de efeito */
  radius?: number;
}

// ============================================
// POWER-UPS E RECOMPENSAS
// ============================================

/**
 * Tipos de power-ups que podem spawnar nas salas
 */
export type PowerUpType = 
  | "health"       // Recupera vida
  | "shield"       // Escudo temporário
  | "ammo"         // Munição especial
  | "speed"        // Boost de velocidade
  | "damage"       // Boost de dano
  | "invincibility" // Invencibilidade temporária

/**
 * Spawn de power-up em uma sala
 */
export interface PowerUpSpawn {
  type: PowerUpType;
  position: THREE.Vector3;
  /** Chance de spawnar (0-1, default 1) */
  spawnChance?: number;
}

/**
 * Tipos de recompensas
 */
export type RewardType = 
  | "chips"        // Moedas do jogo
  | "crystals"     // Cristais premium
  | "xp"           // Experiência
  | "avatar"       // Avatar desbloqueado
  | "tank"         // Tanque desbloqueado
  | "title"        // Título cosmético
  | "skin"         // Skin de tanque

/**
 * Recompensa do dungeon
 */
export interface DungeonReward {
  type: RewardType;
  amount?: number;
  itemId?: string;
}

// ============================================
// CONFIGURAÇÃO DE SAÍDAS (PORTAS)
// ============================================

/**
 * Configuração de uma saída/porta da sala
 */
export interface DungeonExit {
  /** ID único da saída */
  id: string;
  /** Direção cardinal da porta */
  direction?: DoorDirection;
  /** Posição da porta na arena */
  position: THREE.Vector3;
  /** Rotação da porta (em radianos) */
  rotation: number;
  /** ID da sala destino */
  targetRoomId: string;
  /** Tipo de bloqueio */
  lockType: DoorLockType;
  /** Estado inicial da porta */
  initialState: DoorState;
  /** Se é uma porta de retorno (entrada da sala) */
  isReturnPath?: boolean;
  /** Label visual (opcional) */
  label?: string;
  /**
   * ID do item/ability narrativo necessário para desbloquear (Metroidvania).
   * Usado com lockType === "key" ou "secret".
   * O item deve estar em `useStory.narrativeItems`.
   */
  requiredItemId?: string;
  /** Mensagem mostrada quando o jogador não possui o item requerido */
  lockedHint?: string;
}

// ============================================
// CONFIGURAÇÃO DE SALA
// ============================================

/**
 * Configuração de sala de sobrevivência
 */
export interface SurvivalRoomConfig {
  /** Tempo para sobreviver em segundos */
  survivalTimeSeconds: number;
  /** Intervalo de spawn de inimigos em ms */
  spawnIntervalMs: number;
  /** Máximo de inimigos simultâneos */
  maxSimultaneousEnemies: number;
  /** Inimigos ficam mais fortes com o tempo? */
  difficultyScaling: boolean;
  /** Eventos cronometrados durante a sobrevivência */
  timedEvents?: Array<{
    timeSeconds: number;
    message: string;
    enemyBoost?: number;
  }>;
}

/**
 * Configuração de sala de defesa de gerador/alvo
 */
export interface GeneratorDefenseConfig {
  /** HP do gerador */
  generatorHealth: number;
  /** Posição do gerador na arena */
  generatorPosition: THREE.Vector3;
  /** Waves de ataque ao gerador */
  attackWaves: Array<{
    delayMs: number;
    enemyCount: number;
    targetGenerator: boolean; // Se true, inimigos priorizam gerador
  }>;
  /** Regeneração do gerador entre waves (HP) */
  regenBetweenWaves?: number;
  /** Mensagens de alerta por HP */
  healthAlerts: Array<{
    healthPercent: number;
    message: string;
    icon?: string;
  }>;
}

/**
 * Configuração completa de uma sala do dungeon
 */
export interface DungeonRoom {
  /** ID único da sala */
  id: string;
  /** Nome da sala (mostrado no minimap) */
  name: string;
  /** Tipo da sala */
  type: RoomType;
  
  // === Arena Config ===
  /** Tamanho da arena (raio ou lado) */
  arenaSize: number;
  /** Forma da arena */
  arenaShape: ArenaShape;
  /** Tema visual do terreno */
  terrainTheme: TerrainConfig;
  /** Quantidade de obstáculos (0-10) */
  obstaclesDensity?: number;
  
  // === Enemies ===
  /** Waves de inimigos desta sala */
  waves: RoomWaveConfig[];
  
  // === Connections ===
  /** Saídas/portas desta sala */
  exits: DungeonExit[];
  /** Posição de entrada do jogador (spawn point) */
  playerSpawnPosition?: THREE.Vector3;
  
  // === Optional Extras ===
  /** Power-ups que spawnam na sala */
  powerUps?: PowerUpSpawn[];
  /** Perigos ambientais */
  hazards?: EnvironmentHazard[];
  /** ID do diálogo a mostrar ao entrar na sala */
  dialogueId?: string;
  /** Música específica para esta sala */
  musicTrack?: string;
  /** Cor de iluminação especial */
  ambientColor?: string;
  
  // === Modos Especiais ===
  /** Configuração para sala tipo "survival" */
  survivalConfig?: SurvivalRoomConfig;
  /** Configuração para sala tipo "generator" */
  generatorConfig?: GeneratorDefenseConfig;
  /**
   * IDs de itens narrativos concedidos ao completar a sala
   * (Metroidvania: chaves/abilities que destravam outras portas)
   */
  grantsNarrativeItems?: string[];
  /**
   * ID de item narrativo requerido para o jogador poder entrar nesta sala
   * a partir de uma porta com lockType "secret" (gate Metroidvania).
   */
  requiresNarrativeItem?: string;
}

// ============================================
// CONFIGURAÇÃO DO DUNGEON (CAPÍTULO)
// ============================================

/**
 * Configuração completa de um dungeon (capítulo)
 */
export interface DungeonConfig {
  /** ID do capítulo */
  chapterId: number;
  /** Nome do dungeon */
  name: string;
  /** Descrição curta */
  description: string;
  
  // === Rooms ===
  /** Lista de todas as salas */
  rooms: DungeonRoom[];
  /** ID da sala inicial */
  startRoomId: string;
  /** ID da sala do boss (opcional - alguns dungeons não têm boss) */
  bossRoomId?: string;
  
  // === Progression ===
  /** Total de salas no dungeon */
  totalRooms: number;
  /** Tempo estimado em minutos */
  estimatedMinutes: number;
  /** Nível de dificuldade base (1-10) */
  difficultyLevel: number;
  
  // === Rewards ===
  /** Recompensas ao completar o dungeon */
  completionRewards: DungeonReward[];
  /** Salas secretas (opcionais) */
  secretRooms?: DungeonRoom[];
  
  // === Metadata ===
  /** Versão do dungeon (para atualizações) */
  version: string;
}

// ============================================
// ESTADO DO DUNGEON EM RUNTIME
// ============================================

/**
 * Estado de uma sala durante o jogo
 */
export interface RoomRuntimeState {
  /** ID da sala */
  roomId: string;
  /** Se já foi visitada */
  visited: boolean;
  /** Se foi completada (todos inimigos eliminados) */
  completed: boolean;
  /** Estado de cada saída */
  exitStates: Map<string, DoorState>;
  /** Inimigos restantes */
  enemiesRemaining: number;
  /** Wave atual */
  currentWave: number;
  /** Power-ups coletados */
  collectedPowerUps: Set<string>;
}

/**
 * Estado do dungeon durante o jogo
 */
export interface DungeonRuntimeState {
  /** Config do dungeon atual */
  config: DungeonConfig;
  /** ID da sala atual */
  currentRoomId: string;
  /** Salas visitadas */
  visitedRoomIds: Set<string>;
  /** Salas completadas */
  completedRoomIds: Set<string>;
  /** Estado de cada sala */
  roomStates: Map<string, RoomRuntimeState>;
  /** Se está em transição entre salas */
  isTransitioning: boolean;
  /** Se o dungeon foi completado */
  isComplete: boolean;
  /** Tempo total gasto (em segundos) */
  elapsedTime: number;
  /** Recompensas coletadas durante o dungeon */
  collectedRewards: DungeonReward[];
}

// ============================================
// EVENTOS DO SISTEMA DE DUNGEON
// ============================================

/**
 * Tipos de eventos do dungeon
 */
export type DungeonEventType =
  | "room_enter"         // Jogador entrou em uma sala
  | "room_complete"      // Sala completada (todos inimigos mortos)
  | "door_unlock"        // Porta desbloqueada
  | "door_open"          // Porta aberta
  | "wave_start"         // Nova wave começou
  | "wave_complete"      // Wave completada
  | "boss_spawn"         // Boss apareceu
  | "boss_phase_change"  // Boss mudou de fase
  | "boss_defeated"      // Boss derrotado
  | "dungeon_complete"   // Dungeon completado
  | "secret_found"       // Sala secreta descoberta
  | "powerup_collected"  // Power-up coletado

/**
 * Evento do dungeon
 */
export interface DungeonEvent {
  type: DungeonEventType;
  roomId: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

// ============================================
// POSIÇÕES PREDEFINIDAS PARA PORTAS
// ============================================

/**
 * Direções cardinais para posicionamento de portas
 */
export const DOOR_POSITIONS = {
  NORTH: (arenaSize: number) => new THREE.Vector3(0, 0, -(arenaSize / 2 - 0.6)),
  SOUTH: (arenaSize: number) => new THREE.Vector3(0, 0, arenaSize / 2 - 0.6),
  EAST: (arenaSize: number) => new THREE.Vector3(arenaSize / 2 - 0.6, 0, 0),
  WEST: (arenaSize: number) => new THREE.Vector3(-(arenaSize / 2 - 0.6), 0, 0),
} as const;

/**
 * Rotações para portas baseadas na direção
 */
export const DOOR_ROTATIONS = {
  NORTH: 0,
  SOUTH: Math.PI,
  EAST: -Math.PI / 2,
  WEST: Math.PI / 2,
} as const;

export const OPPOSITE_DOOR_DIRECTION: Record<DoorDirection, DoorDirection> = {
  NORTH: "SOUTH",
  SOUTH: "NORTH",
  EAST: "WEST",
  WEST: "EAST",
};

export function getOppositeDoorDirection(direction: DoorDirection): DoorDirection {
  return OPPOSITE_DOOR_DIRECTION[direction];
}

export function inferDoorDirectionFromRotation(rotation: number): DoorDirection {
  const normalized = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const angleMap: Array<{ direction: DoorDirection; angle: number }> = [
    { direction: "NORTH", angle: 0 },
    { direction: "SOUTH", angle: Math.PI },
    { direction: "EAST", angle: (Math.PI * 3) / 2 },
    { direction: "WEST", angle: Math.PI / 2 },
  ];

  let best = angleMap[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const entry of angleMap) {
    const distance = Math.abs(normalized - entry.angle);
    const wrappedDistance = Math.min(distance, Math.PI * 2 - distance);
    if (wrappedDistance < bestDistance) {
      bestDistance = wrappedDistance;
      best = entry;
    }
  }

  return best.direction;
}

// ============================================
// HELPERS
// ============================================

/**
 * Cria uma posição Vector3 a partir de coordenadas simples
 */
export function pos(x: number, y: number, z: number): THREE.Vector3 {
  return new THREE.Vector3(x, y, z);
}

/**
 * Verifica se uma sala é do tipo combate
 */
export function isCombatRoom(room: DungeonRoom): boolean {
  return room.type === "combat" || room.type === "miniboss" || room.type === "boss";
}

/**
 * Calcula o total de inimigos de uma sala
 */
export function getRoomTotalEnemies(room: DungeonRoom): number {
  return room.waves.reduce((total, wave) => {
    if (wave.enemyCount > 0) {
      return total + wave.enemyCount;
    }
    if (wave.bossConfig) {
      return total + 1;
    }
    return total;
  }, 0);
}

/**
 * Verifica se o dungeon tem salas secretas
 */
export function hasSecretRooms(config: DungeonConfig): boolean {
  return !!config.secretRooms && config.secretRooms.length > 0;
}
